// src/hooks/useOfflineSession.js
// ─────────────────────────────────────────────────────────────────────────────
//  Hook لاستعادة الجلسة والبيانات عند عدم الاتصال بالإنترنت
//  ضعه في أعلى شجرة المكونات (App.jsx أو main.jsx)
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect } from 'react';
import offlineStorage from '../services/offlineStorage';

/**
 * يُرجع:
 *   isOnline       — هل الاتصال متاح حالياً
 *   offlineUser    — بيانات المستخدم من الكاش (null إن لم يوجد)
 *   offlineData    — { requests, conversations } من الكاش
 *   isOfflineReady — هل انتهى تحميل البيانات من الكاش
 */
export function useOfflineSession() {
  const [isOnline,       setIsOnline]       = useState(navigator.onLine);
  const [offlineUser,    setOfflineUser]    = useState(null);
  const [offlineData,    setOfflineData]    = useState({ requests: null, conversations: null });
  const [isOfflineReady, setIsOfflineReady] = useState(false);

  // ── مراقبة حالة الاتصال
  useEffect(() => {
    const handleOnline  = () => {
      console.log('🌐 [Offline] Connection restored');
      setIsOnline(true);
    };
    const handleOffline = () => {
      console.log('📴 [Offline] Connection lost — loading cached data...');
      setIsOnline(false);
    };

    window.addEventListener('online',  handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online',  handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // ── تحميل البيانات من الكاش عند عدم الاتصال
  useEffect(() => {
    if (isOnline) {
      // متصل — لا حاجة للكاش
      setIsOfflineReady(true);
      return;
    }

    // غير متصل — نحمّل من IndexedDB
    async function loadOfflineData() {
      console.log('💾 [Offline] Loading session from IndexedDB...');

      try {
        // 1. استعادة الجلسة
        const cached = await offlineStorage.loadSession();

        if (!cached) {
          console.warn('⚠️ [Offline] No cached session found — user must login online first');
          setIsOfflineReady(true);
          return;
        }

        const { session, userId } = cached;
        setOfflineUser(session.user || null);

        console.log('✅ [Offline] Session restored for user:', userId.substring(0, 8) + '...');

        // 2. استعادة الطلبات والمحادثات بالتوازي
        const [requests, conversations] = await Promise.all([
          offlineStorage.loadRequests(userId),
          offlineStorage.loadConversations(userId),
        ]);

        setOfflineData({ requests, conversations });

        console.log('✅ [Offline] Data loaded:', {
          requests:      requests?.length      ?? 0,
          conversations: conversations?.length ?? 0,
        });
      } catch (err) {
        console.error('❌ [Offline] Failed to load offline data:', err);
      } finally {
        setIsOfflineReady(true);
      }
    }

    loadOfflineData();
  }, [isOnline]);

  return { isOnline, offlineUser, offlineData, isOfflineReady };
}

/**
 * دالة مساعدة: هل المستخدم مسجل دخول حتى بدون إنترنت؟
 * استخدمها في أي مكوّن يحتاج التحقق
 */
export async function checkOfflineAuth() {
  const cached = await offlineStorage.loadSession();
  return cached ? { isLoggedIn: true, user: cached.session.user, userId: cached.userId } : { isLoggedIn: false };
}